import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";

const STATUS_COLORS = { "Awaiting Vendor Confirmation": "bg-amber-100 text-amber-700", "Vendor Confirmed": "bg-indigo-100 text-indigo-700", "Quote Sent": "bg-blue-100 text-blue-700", "Counter Offered": "bg-orange-100 text-orange-700", "Approved": "bg-green-100 text-green-700", "Confirmed": "bg-green-100 text-green-700", "Cancelled": "bg-red-100 text-red-700", "Declined by Vendor": "bg-red-100 text-red-700" };

function Negotiation({ log }) {
  if (!log?.length) return null;
  return (
    <div className="mt-3 rounded-xl bg-secondary p-3">
      <p className="text-xs font-bold text-midnight mb-2">Negotiation history</p>
      <div className="space-y-1.5">
        {log.map((n, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className={`font-semibold ${n.by === "vendor" ? "text-mart-wood" : "text-midnight"}`}>{n.by === "vendor" ? "Vendor" : "You"} {n.message ? `— ${n.message}` : ""}</span>
            <span className="font-bold">{inr(n.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Customizations() {
  const [list, setList] = useState([]);
  const [counterFor, setCounterFor] = useState(null);
  const [counterAmt, setCounterAmt] = useState("");
  const [counterMsg, setCounterMsg] = useState("");
  const load = () => api.get("/customization-requests").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/customization-requests/${id}/approve`); toast.success("Quote approved — order confirmed!"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };
  const sendCounter = async (id) => {
    if (!counterAmt || Number(counterAmt) <= 0) { toast.error("Enter a counter amount"); return; }
    try { await api.post(`/customization-requests/${id}/counter`, { amount: Number(counterAmt), message: counterMsg }); toast.success("Counter offer sent to vendor"); setCounterFor(null); setCounterAmt(""); setCounterMsg(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send counter"); }
  };

  return (
    <div>
      <h1 className="text-2xl font-extrabold text-midnight mb-2">Furniture Customization Requests</h1>
      <p className="mb-6 text-sm text-muted-foreground">Flow: Request → Vendor Confirmation → Quote → (Negotiate) → Your Approval → Order</p>
      {list.length === 0 ? <p className="text-muted-foreground">No customization requests yet.</p> : (
        <div className="space-y-4" data-testid="customizations-list">
          {list.map((c) => (
            <div key={c.id} className="rounded-3xl border border-border p-5" data-testid={`customization-${c.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString("en-IN")}</p>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_COLORS[c.status] || "bg-secondary"}`}>{c.status}</span>
              </div>
              <p className="mt-2 text-sm text-midnight"><b>Details:</b> {c.details}</p>
              {c.finish && <p className="text-sm text-muted-foreground">Finish: {c.finish}</p>}
              <Negotiation log={c.negotiation} />

              {c.quote != null && c.status === "Quote Sent" && (
                <div className="mt-3 rounded-2xl bg-secondary p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div><p className="text-xs text-muted-foreground">Vendor quote</p><p className="text-xl font-extrabold text-mart-wood">{inr(c.quote)}</p></div>
                    <div className="ml-auto flex gap-2">
                      <button onClick={() => setCounterFor(counterFor === c.id ? null : c.id)} data-testid={`counter-toggle-${c.id}`} className="rounded-full border border-border px-4 py-2.5 text-sm font-bold text-midnight">Counter Offer</button>
                      <button onClick={() => approve(c.id)} data-testid={`approve-quote-${c.id}`} className="flex items-center gap-2 rounded-full bg-mart-home px-5 py-2.5 font-bold text-white"><Check className="h-4 w-4" /> Approve & Confirm</button>
                    </div>
                  </div>
                  {counterFor === c.id && (
                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                      <div className="flex items-center rounded-full border border-border bg-white px-3 py-2"><span className="text-sm text-muted-foreground">₹</span><input type="number" value={counterAmt} onChange={(e) => setCounterAmt(e.target.value)} placeholder="Your offer" data-testid={`counter-amount-${c.id}`} className="ml-1 w-28 bg-transparent text-sm outline-none" /></div>
                      <input value={counterMsg} onChange={(e) => setCounterMsg(e.target.value)} placeholder="Message (optional)" data-testid={`counter-message-${c.id}`} className="flex-1 min-w-[160px] rounded-full border border-border px-4 py-2.5 text-sm outline-none" />
                      <button onClick={() => sendCounter(c.id)} data-testid={`counter-send-${c.id}`} className="rounded-full bg-midnight px-5 py-2.5 text-sm font-bold text-white">Send Counter</button>
                    </div>
                  )}
                </div>
              )}
              {c.status === "Counter Offered" && <p className="mt-3 text-sm font-semibold text-orange-700">Your counter of {inr(c.counter_amount)} was sent — awaiting vendor response.</p>}
              {(c.status === "Approved" || c.status === "Confirmed") && <p className="mt-3 text-sm font-semibold text-mart-home">✓ Quote of {inr(c.quote)} approved — your custom order is confirmed.</p>}
              {c.status === "Declined by Vendor" && <p className="mt-3 text-sm font-semibold text-red-600">The vendor was unable to take this request.</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
